/**
 * Helper for Question-tab Response Absolute / Percentage stacked bar charts.
 * Used below the Trend card on RHS — does not alter Trends tab or Trend line chart.
 */

export type TrendBarLevel = 'top' | 'mid' | 'bottom';

export interface TrendBarSegmentCounts {
  top: number;
  mid: number;
  bottom: number;
  response: number;
}

export interface TrendBarHeaderLabels {
  top?: string;
  mid?: string;
  bottom?: string;
}

export interface BuildQuestionTrendBarChartParams {
  labels: string[];
  segments: TrendBarSegmentCounts[];
  headerLabels: TrendBarHeaderLabels;
  isPercentage: boolean;
  xText: string;
  yText?: string;
}

export interface StackedSeriesDataset {
  label: string;
  data: number[];
  backgroundColor: string;
  maxBarThickness?: number;
}

export interface BuildStackedSeriesBarChartParams {
  labels: string[];
  datasets: StackedSeriesDataset[];
  isPercentage: boolean;
  xText: string;
}

const LEVEL_COLORS: Record<TrendBarLevel, string> = {
  top: '#0B7139',
  mid: '#FAB416',
  bottom: '#ED2424',
};

export function computeTrendBarValue(
  value: number,
  total: number,
  percentage: boolean
): number | string {
  if (!percentage) {
    return value || 0;
  }
  return total > 0 ? ((value / total) * 100).toFixed(1) : '0';
}

export function getTrendBarColorByLevel(level: TrendBarLevel): string {
  return LEVEL_COLORS[level] || '#000000';
}

/**
 * Builds T3B/MB/B3B chart config for rating Response Absolute/Percentage view.
 */
export function buildQuestionTrendBarChartConfig(
  params: BuildQuestionTrendBarChartParams
): {
  labels: string[];
  xText: string;
  dataset: Array<{
    label: string;
    data: Array<number | string>;
    backgroundColor: string;
    maxBarThickness: number;
  }>;
  showPercentage: boolean;
  responses: number[];
} {
  const { labels, segments, headerLabels, isPercentage, xText } = params;
  const levels: TrendBarLevel[] = ['top', 'mid', 'bottom'];

  const dataset = levels
    .map((level) => {
      const label = headerLabels?.[level];
      if (!label) {
        return null;
      }
      return {
        label,
        data: [] as Array<number | string>,
        backgroundColor: getTrendBarColorByLevel(level),
        maxBarThickness: 60,
      };
    })
    .filter(Boolean) as Array<{
    label: string;
    data: Array<number | string>;
    backgroundColor: string;
    maxBarThickness: number;
  }>;

  const chartLabels: string[] = [];
  const responses: number[] = [];
  segments.forEach((segment, index) => {
    const total = segment?.response || 0;
    if (total <= 0) {
      return;
    }
    chartLabels.push(labels[index]);
    responses.push(total);
    const levelValues: Record<TrendBarLevel, number> = {
      top: segment.top || 0,
      mid: segment.mid || 0,
      bottom: segment.bottom || 0,
    };
    let datasetIndex = 0;
    levels.forEach((level) => {
      if (!headerLabels?.[level]) {
        return;
      }
      dataset[datasetIndex].data.push(
        computeTrendBarValue(levelValues[level], total, isPercentage)
      );
      datasetIndex += 1;
    });
  });

  return {
    labels: chartLabels,
    xText: xText || '',
    dataset,
    showPercentage: isPercentage,
    responses,
  };
}

/**
 * Builds Absolute/Percentage stacked bars from multi-select (or similar) series.
 * Percentage = share of each option within that time bucket's total.
 */
export function buildStackedSeriesBarChartConfig(
  params: BuildStackedSeriesBarChartParams
): {
  labels: string[];
  xText: string;
  dataset: Array<{
    label: string;
    data: Array<number | string>;
    backgroundColor: string;
    maxBarThickness: number;
  }>;
  showPercentage: boolean;
  responses: number[];
} {
  const { labels, datasets, isPercentage, xText } = params;
  const labelCount = labels?.length || 0;
  const source = (datasets || []).filter((d) => d && Array.isArray(d.data));

  const dataset = source.map((series) => ({
    label: series.label,
    data: [] as Array<number | string>,
    backgroundColor: series.backgroundColor,
    maxBarThickness: series.maxBarThickness ?? 60,
  }));

  const responses: number[] = [];
  for (let i = 0; i < labelCount; i++) {
    const total = source.reduce((sum, series) => sum + (Number(series.data[i]) || 0), 0);
    responses.push(total);
    source.forEach((series, seriesIndex) => {
      const value = Number(series.data[i]) || 0;
      dataset[seriesIndex].data.push(computeTrendBarValue(value, total, isPercentage));
    });
  }

  return {
    labels: labels || [],
    xText: xText || '',
    dataset,
    showPercentage: isPercentage,
    responses,
  };
}

/**
 * Parses formatted or raw count strings from getRatingBasedData into numbers.
 */
export function parseTrendBarCount(value: any): number {
  if (value == null || value === '') {
    return 0;
  }
  if (typeof value === 'number') {
    return value;
  }
  const cleaned = String(value).replace(/,/g, '').trim();
  const parsed = Number(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}
